/* AddAccountFlow — guided creation + connection of one account. Route /accounts/new.
   intro → confirm → proxy → login → created.
   The account row (and its connection key) is created on "Yes" at the confirm step;
   bailing afterwards leaves a resumable pending account. The platform (OnlyFans or
   Fansly) is chosen before creation and drives which login the agent runs.

   The connection step is a *streamed* login (see ../../../../streamed-login-agent):
   the operator sources a proxy, then logs in through a browser hosted on our login
   agent and relayed here — no desktop app, no pasted key. The old download-app /
   paste-token / poll flow is retired. */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAccounts } from '../../context/AccountsContext';
import { OnboardingStage } from './OnboardingStage';
import { Panel, Head, Title, Btn, Icon, C, Pose } from './onboardingKit';
import { ProxyPicker } from './ProxyPicker';
import { LoginStream } from './LoginStream';
import { PlatformLogo } from '../PlatformLogo';
import { Lockup } from '../../brand/Brand';
import { BusyLabel } from '../Spinner';

type Step = 'intro' | 'confirm' | 'proxy' | 'login' | 'created';
type Platform = 'onlyfans' | 'fansly';

const PLATFORM_LABEL: Record<Platform, string> = { onlyfans: 'OnlyFans', fansly: 'Fansly' };

/** Two-tab platform picker shown before the account is created. */
function PlatformTabs({ value, onChange, disabled }: { value: Platform; onChange: (p: Platform) => void; disabled?: boolean }) {
  return (
    <div>
      <div style={{ color: C.muted, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Platform</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {(['onlyfans', 'fansly'] as Platform[]).map((p) => {
          const active = value === p;
          return (
            <button
              key={p}
              type="button"
              disabled={disabled}
              onClick={() => onChange(p)}
              style={{
                flex: 1, fontFamily: 'inherit', fontWeight: 700, fontSize: 14, borderRadius: 12, padding: '11px 14px',
                cursor: disabled ? 'default' : 'pointer',
                border: `1px solid ${active ? C.purple : C.line2}`,
                background: active ? 'color-mix(in srgb, var(--om-accent) 15%, transparent)' : 'transparent',
                color: active ? '#fff' : C.muted,
                transition: 'all .15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <PlatformLogo platform={p} size={16} />
              {PLATFORM_LABEL[p]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const POSE_FOR: Record<Step, Pose> = {
  intro: 'wave', confirm: 'point', proxy: 'explain', login: 'point', created: 'okay',
};
const DOT_INDEX: Partial<Record<Step, number>> = { proxy: 0, login: 1, created: 2 };

export function AddAccountFlow() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Leaving the flow: bring the shell's Accounts list up to date with what changed here.
  const { refresh: refreshAccounts } = useAccounts();
  useEffect(() => () => { void refreshAccounts(); }, [refreshAccounts]);
  // ?resume=<accountId> re-enters the flow for a pending account: skip create,
  // go straight to sourcing a proxy + logging in.
  const resumeId = searchParams.get('resume');
  // ?from=onboarding means Sofi just introduced herself in the new-user flow, so
  // skip the standalone intro and go straight to the confirm gate.
  const fromOnboarding = searchParams.get('from') === 'onboarding';

  const [step, setStep] = useState<Step>(resumeId ? 'proxy' : fromOnboarding ? 'confirm' : 'intro');
  const [animKey, setAnimKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(resumeId);
  const [platform, setPlatform] = useState<Platform>('onlyfans');
  const [connected, setConnected] = useState<{ username?: string | null; avatar_url?: string | null } | null>(null);

  const go = (s: Step) => { setStep(s); setAnimKey((k) => k + 1); };
  const toDashboard = () => navigate('/dashboard', { replace: true });

  const dotN = 3;
  const dotIndex = DOT_INDEX[step] ?? 0;
  const stepLabel = t('common.step', { i: dotIndex + 1, n: dotN });

  // Create the account (mints the connection key via DB trigger), then pick a proxy.
  const createAndAdvance = async () => {
    setCreating(true);
    setError(null);
    try {
      const account = await api.createAccount({ platform });
      setAccountId(account.account_id);
      go('proxy');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('addAccount.errorCreate'));
    } finally {
      setCreating(false);
    }
  };

  // When the streamed login reports success, pull the identity the Keeper synced
  // so the "created" screen can show the real @username.
  const onLoginConnected = async () => {
    try {
      if (accountId) {
        const s = await api.getAccountConnectionStatus(accountId);
        setConnected({ username: s.username, avatar_url: s.avatar_url });
      }
    } catch { /* identity is best-effort; created screen falls back to generic */ }
    go('created');
  };

  const connectedName = connected?.username ? `@${connected.username}` : t('addAccount.yourAccount');

  let content: React.ReactNode = null;

  if (step === 'intro') content = (
    <Panel anim={animKey} width={500}>
      <Lockup height={13} />
      <h1 style={{ margin: '20px 0 0', color: C.text, fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em' }}>{t('addAccount.introTitle')}</h1>
      <p style={{ color: C.muted, fontSize: 16, lineHeight: 1.6, margin: '13px 0 0' }}>{t('addAccount.introSub')}</p>
      <div style={{ marginTop: 22 }}><PlatformTabs value={platform} onChange={setPlatform} disabled={creating} /></div>
      {error && <div style={{ color: '#ff6b6b', fontSize: 13.5, marginTop: 14 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
        <Btn onClick={createAndAdvance} disabled={creating} icon={creating ? undefined : 'arrow'}>
          <BusyLabel busy={creating} label={t('addAccount.creating')}>{t('addAccount.introCta')}</BusyLabel>
        </Btn>
        <Btn kind="ghost" onClick={toDashboard} disabled={creating}>{t('addAccount.cancel')}</Btn>
      </div>
    </Panel>
  );
  else if (step === 'confirm') content = (
    <Panel anim={animKey} width={480}>
      <div style={{ width: 52, height: 52, borderRadius: 14, background: 'color-mix(in srgb, var(--om-accent) 18%, transparent)', color: C.purpleHi, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}><Icon name="plus" size={26} /></div>
      <Title h={t('addAccount.confirmTitle')} sub={t('addAccount.confirmSub')} />
      <div style={{ margin: '4px 0 18px' }}><PlatformTabs value={platform} onChange={setPlatform} disabled={creating} /></div>
      {error && <div style={{ color: '#ff6b6b', fontSize: 13.5, marginBottom: 14 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
        <Btn onClick={createAndAdvance} disabled={creating} icon={creating ? undefined : 'arrow'}>
          <BusyLabel busy={creating} label={t('addAccount.creating')}>{t('addAccount.confirmYes')}</BusyLabel>
        </Btn>
        <Btn kind="ghost" onClick={toDashboard} disabled={creating}>{t('addAccount.cancel')}</Btn>
      </div>
    </Panel>
  );
  else if (step === 'proxy' && accountId) content = (
    <Panel anim={animKey} width={520}>
      <Head n={dotN} i={dotIndex} label={stepLabel} />
      <Title h="Choose a connection proxy" sub="Your account logs in through this proxy and stays bound to it." />
      <ProxyPicker accountId={accountId} variant="first" onSourced={() => go('login')} onCancel={toDashboard} />
    </Panel>
  );
  else if (step === 'login' && accountId) content = (
    <Panel anim={animKey} width={836}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={() => go('proxy')} />
      <Title h={`Log in to ${PLATFORM_LABEL[platform]}`} sub="Sign in below — including 2FA if asked. Press Done once you’re on your account." />
      <LoginStream accountId={accountId} onConnected={onLoginConnected} onStopped={() => go('proxy')} />
    </Panel>
  );
  else if (step === 'created') content = (
    <Panel anim={animKey} width={520}>
      <div style={{ width: 54, height: 54, borderRadius: '50%', background: '#1f8a5b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(0,0,0,.5)', animation: 'om-pop .35s ease' }}><Icon name="check" size={28} /></div>
      <h1 style={{ color: C.text, fontSize: 26, fontWeight: 800, margin: '18px 0 0' }}>{t('addAccount.createdTitle')}</h1>
      <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.55, margin: '11px 0 0' }}>
        <b style={{ color: C.text }}>{connectedName}</b> {t('addAccount.createdSub')}
      </p>
      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <Btn onClick={() => navigate(`/account/${accountId}/ai-setup`, { replace: true })} icon="arrow">{t('addAccount.continueToAi')}</Btn>
        <Btn kind="ghost" onClick={toDashboard}>{t('addAccount.goToDashboard')}</Btn>
      </div>
    </Panel>
  );

  // Guard: a step that needs an accountId but has none (e.g. deep-linked) falls back.
  useEffect(() => {
    if ((step === 'proxy' || step === 'login') && !accountId) go('intro');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, accountId]);

  // Resuming a pending account skips the platform picker, so hydrate it from the
  // account row — the login title (and everything downstream) tracks the real one.
  useEffect(() => {
    if (!resumeId) return;
    let active = true;
    (async () => {
      try {
        const acc = await api.getAccount(resumeId);
        if (active && acc?.platform) setPlatform(acc.platform);
      } catch { /* title falls back to OnlyFans; backend still uses the real platform */ }
    })();
    return () => { active = false; };
  }, [resumeId]);

  return (
    <OnboardingStage pose={POSE_FOR[step]} onClose={toDashboard}>
      {content}
    </OnboardingStage>
  );
}
